import type {
  ScopeApiError,
  KnowledgeBaseChoice,
  ScopeAgentMode,
  ScopeAgentRunView,
  ScopeAgentStatus,
  ScopeDescription,
  ScopeCitation,
  ScopeRequirement,
  ScopeSource,
  ScopeLevel,
  ScopeLine as PackageLine,
  ScopePackageStep,
  ScopePackage,
  ScopeStandardProfile,
  ScopeRunForm,
  ScopeStandardSetup,
  ScopeStructure,
  ScopeStructureKind,
  StartScopeAgentRun,
} from '@/features/scope-breakdown/types';
import { SCOPE_CONTRACT_PLACEMENT } from '@/features/scope-breakdown/types';
import type { useScopeBreakdownPreparation } from '@/features/scope-breakdown/queries';
import type { CellInput } from '@/features/sheets/contract/cells';
import type {
  CellView,
  ColumnView,
  NoteKind,
  Reference,
  RowView,
  SheetSnapshot,
  TabView,
} from '@/features/sheets/contract/schema';
import { cellClipboardText, cellNote, peopleEditable } from '@/features/sheets/utils';
import { pageShapes } from '@/features/documents/preview/shapes';
import { DOCUMENT_EXTENSION_GROUPS } from '@/features/documents/constants';
import { hasExtension } from '@/features/documents/utils';
import { t } from '@/features/i18n/utils';

export const isScopeAgentRunActive = (status?: ScopeAgentStatus) =>
  status?.run?.state === 'running';

export const scopeSheetState = (status?: ScopeAgentStatus) => {
  if (!status || status.available === false || status.artifactId) return null;
  return isScopeAgentRunActive(status) ? 'running' : 'missing';
};

export const scopeRunEnded = (previous: ScopeAgentStatus | undefined, next: ScopeAgentStatus) => {
  if (!previous || !next.run || isScopeAgentRunActive(next)) return false;
  return previous.run?.id !== next.run.id || isScopeAgentRunActive(previous);
};

export const scopeRunSummary = ({ run }: ScopeAgentStatus) => {
  if (!run) return null;
  if (run.state === 'running') return { state: 'running' } as const;
  if (run.state === 'failed') return { state: 'failed', reason: run.reason } as const;
  if (run.outcome?.answerStatus !== 'partial') return { state: 'completed' } as const;
  const files = run.outcome.uncovered.map(({ docId, filename }) => ({
    docId,
    filename,
    kind: hasExtension(filename, [
      ...DOCUMENT_EXTENSION_GROUPS.schedules,
      ...DOCUMENT_EXTENSION_GROUPS.billsOfQuantities,
      ...DOCUMENT_EXTENSION_GROUPS.models,
      ...DOCUMENT_EXTENSION_GROUPS.drawings,
      ...DOCUMENT_EXTENSION_GROUPS.emails,
    ])
      ? 'unpaged'
      : 'unread',
  }));
  const retryableCount = files.filter((file) => file.kind === 'unread').length;
  return { state: 'partial', files, retryableCount } as const;
};

export const shownSheetId = (status: ScopeAgentStatus) => {
  if (status.artifactId || !isScopeAgentRunActive(status)) return status.artifactId;
  return status.run?.artifactId ?? null;
};

export const scopePageState = (
  status: ScopeAgentStatus,
  preparation: ReturnType<typeof useScopeBreakdownPreparation>,
) => {
  if (status.available === false) return 'unavailable';
  if (isScopeAgentRunActive(status)) return 'running';
  if (status.artifactId) return 'sheet';
  if (preparation === 'failed') return 'failed';
  if (preparation === 'missing' || preparation === 'unavailable') return 'no-documents';
  if (preparation === 'preparing' || preparation === 'loading') return 'preparing';
  return 'empty';
};

export const scopeFrameState = (status: ScopeAgentStatus) => {
  if (status.available === false) return 'unavailable';
  const summary = scopeRunSummary(status);
  if (summary?.state === 'running' || summary?.state === 'failed') return summary.state;
  if (!status.artifactId) return 'none';
  return summary?.state === 'partial' ? 'partial' : 'ready';
};

export const newSheetForm = (): ScopeRunForm => ({
  mode: 'run',
  structure: 'library',
  documentIds: [],
  knowledgeBaseIds: [],
  instructions: '',
});

export const initialRunForm = ({
  status,
  instructions = '',
  setup,
}: {
  status: ScopeAgentStatus;
  instructions?: string;
  setup?: ScopeStandardSetup['setup'];
}): ScopeRunForm => {
  if (!status.artifactId && setup)
    return {
      ...newSheetForm(),
      mode: 'update',
      structure: setup.structure.kind,
      documentIds: setup.structure.kind === 'template' ? setup.structure.documentIds : [],
      knowledgeBaseIds: setup.knowledgeBaseIds,
      instructions,
    };
  if (!status.artifactId) {
    const failed = status.run?.state === 'failed' ? status.run : null;
    return {
      ...newSheetForm(),
      structure: failed?.structure?.kind ?? 'library',
      documentIds: failed?.structure?.kind === 'template' ? failed.structure.documentIds : [],
      knowledgeBaseIds: failed?.knowledgeBaseIds ?? [],
      instructions: instructions || failed?.instructions || '',
    };
  }
  const ready = new Set(status.knowledgeBases.map((knowledgeBase) => knowledgeBase.id));
  return {
    ...newSheetForm(),
    mode: 'update',
    structure: status.run?.structure ? null : 'library',
    knowledgeBaseIds: status.run?.knowledgeBaseIds.filter((id) => ready.has(id)) ?? [],
    instructions,
  };
};

export const scopeStandardSelection = (
  ready: KnowledgeBaseChoice[],
  selected: string[],
  uploaded: { id: string }[],
) => {
  const readyIds = new Set(ready.map((kb) => kb.id));
  return {
    selectedIds: [
      ...new Set([
        ...selected.filter((id) => readyIds.has(id)),
        ...uploaded.filter((kb) => readyIds.has(kb.id)).map((kb) => kb.id),
      ]),
    ],
    pendingIds: uploaded.filter((kb) => !readyIds.has(kb.id)).map((kb) => kb.id),
  };
};

export const runStructureKind = (
  form: ScopeRunForm,
  status: ScopeAgentStatus,
): ScopeStructureKind | null => form.structure ?? status.run?.structure?.kind ?? null;

export const runFormProblem = (
  form: ScopeRunForm,
  status: ScopeAgentStatus,
): 'pickStructure' | 'pickStandard' | 'pickFiles' | null => {
  const kind = runStructureKind(form, status);
  if (!kind) return 'pickStructure';
  if (form.mode === 'run' && kind === 'tender_bill' && !status.hasTenderBill)
    return 'pickStructure';
  if (kind === 'library' && form.knowledgeBaseIds.length === 0) return 'pickStandard';
  if (form.structure === 'template' && form.documentIds.length === 0) return 'pickFiles';
  return null;
};

export const runReplacesSheet = ({
  status,
  mode,
}: {
  status: ScopeAgentStatus;
  mode: ScopeAgentMode;
}) => mode === 'run' && !!status.artifactId;

const runStructure = (kind: ScopeStructureKind, documentIds: string[]): ScopeStructure =>
  kind === 'template' ? { kind, documentIds } : { kind };

export const runRequest = (form: ScopeRunForm): StartScopeAgentRun => {
  const instructions = form.instructions.trim();
  const optional = {
    ...(instructions ? { instructions } : {}),
  };
  const structure = form.structure ? runStructure(form.structure, form.documentIds) : null;
  if (form.mode === 'run')
    return {
      mode: 'run',
      structure,
      knowledgeBaseIds: form.structure === 'library' ? form.knowledgeBaseIds : [],
      ...optional,
    };
  return {
    mode: 'update',
    ...(structure ? { structure } : {}),
    knowledgeBaseIds: form.knowledgeBaseIds,
    ...optional,
  };
};

export const structureName = (
  run: Pick<ScopeAgentRunView, 'structure' | 'knowledgeBaseIds'>,
  knowledgeBases: KnowledgeBaseChoice[],
): { kind: ScopeStructureKind; names: string[] } | null => {
  if (!run.structure) return null;
  const names = new Map(
    knowledgeBases.map((knowledgeBase) => [knowledgeBase.id, knowledgeBase.name]),
  );
  return {
    kind: run.structure.kind,
    names:
      run.structure.kind === 'library'
        ? run.knowledgeBaseIds.flatMap((id) => names.get(id) ?? [])
        : [],
  };
};

export const scopeRunNotReady = (
  detail:
    | {
        readiness: {
          resources: {
            blocker: 'indexing' | 'indexing_failed' | 'no_documents' | 'graph_not_ready' | null;
          }[];
        };
      }
    | string
    | unknown[]
    | undefined,
) => {
  if (!detail || typeof detail !== 'object' || !('readiness' in detail)) return null;
  const blocker = detail.readiness.resources.find((resource) => resource.blocker)?.blocker;
  if (blocker === 'graph_not_ready') return 'noSheet';
  if (blocker === 'no_documents') return 'noDocuments';
  return blocker ? 'preparing' : null;
};

export type ScopeRowState = 'stated' | 'assumed' | 'input' | 'unsourced';

export type ScopeField = {
  columnId: string;
  column: ColumnView;
  key: string;
  label: string;
  cell: CellView;
  note: { text: string; kind: NoteKind } | null;
};

export type ScopeFlag = ScopeField & { note: { text: string; kind: NoteKind } };

export type ScopeNode = {
  id: string;
  tabId: string;
  parent: string | null;
  depth: number;
  code: string;
  title: string;
  count: number;
  rows: number;
  children: number;
  packages: Set<string>;
  conditions: number;
  input: boolean;
  assumed: boolean;
  interface: boolean;
};

export type ScopeLine = {
  row: RowView;
  tabId: string;
  code: string;
  title: string;
  scope: string;
  description: string;
  unit: string;
  quantity: number | null;
  quantityNote: ReturnType<typeof cellNote>;
  state: ScopeRowState;
  note: boolean;
  intro: boolean;
  userAdded: boolean;
  excluded: boolean;
  interface: boolean;
  sources: Reference[];
  fields: ScopeField[];
  flags: ScopeFlag[];
  tags: string[];
};

export type ScopeFlagSection = {
  node: ScopeNode;
  assumptions: number;
  inputs: number;
  lines: ScopeLine[];
};

export type ScopeConditions = { node: ScopeNode; lines: ScopeLine[] };

export type ScopeItem = { chapter: ScopeNode; group: ScopeNode | null } & (
  | { kind: 'heading'; node: ScopeNode }
  | { kind: 'line'; line: ScopeLine }
  | { kind: 'flags'; section: ScopeFlagSection }
  | { kind: 'conditions'; section: ScopeConditions }
  | { kind: 'intro'; line: ScopeLine }
);

type ScopeOutline = { nodes: ScopeNode[]; items: ScopeItem[] };

const textOf = (cell: CellView | undefined) => (cell?.type === 'text' ? cell.value.trim() : '');

const optionsOf = (cell: CellView | undefined) => (cell?.type === 'enum' ? cell.value : []);

const numberOf = (cell: CellView | undefined) => (cell?.type === 'number' ? cell.value : null);

const NOT_SCOPE_TABS = new Set(['risks', 'files']);

const isUserTyped = (cell: CellView | undefined) =>
  !!cell && cell.type !== 'invalid' && cell.origin === 'user' && hasValue(cell);

export const scopeTabs = (snapshot: SheetSnapshot) =>
  snapshot.tabs.filter((tab) => !tab.key || !NOT_SCOPE_TABS.has(tab.key));

export const scopeColumn = (tab: TabView, key: string) =>
  tab.columns.find((column) => column.key === key);

export const scopePackages = (snapshot: SheetSnapshot) => [
  ...new Set(scopeTabs(snapshot).flatMap((tab) => scopeColumn(tab, 'package')?.options ?? [])),
];

const referencesOf = (row: RowView) =>
  Object.values(row.cells).flatMap((cell) => (cell.type === 'reference' ? cell.value : []));

const scopeRowState = (row: RowView, statusColumnId: string | undefined): ScopeRowState => {
  const kinds = Object.values(row.cells).map((cell) => cellNote(cell)?.kind);
  if (statusColumnId && optionsOf(row.cells[statusColumnId]).includes('clarify')) return 'input';
  if (kinds.includes('input_needed')) return 'input';
  if (kinds.includes('assumption')) return 'assumed';
  return referencesOf(row).length ? 'stated' : 'unsourced';
};

const placementOf = (key: string) =>
  key in SCOPE_CONTRACT_PLACEMENT
    ? SCOPE_CONTRACT_PLACEMENT[key as keyof typeof SCOPE_CONTRACT_PLACEMENT]
    : 'field';

const hasValue = (cell: CellView | undefined) =>
  cellClipboardText(cell, 'en', () => '').trim() !== '';

const scopeFields = (tab: TabView, row: RowView): ScopeField[] =>
  tab.columns.flatMap((column) => {
    const cell = row.cells[column.id];
    if (!cell || column.hidden || column.key === 'title') return [];
    const note = cellNote(cell);
    const field = placementOf(column.key) === 'field' && column.type !== 'reference';
    if (!note && !(field && hasValue(cell))) return [];
    return [{ columnId: column.id, column, key: column.key, label: column.label, cell, note }];
  });

export const scopeSuggestions = (tab: TabView, row: RowView) =>
  tab.columns.flatMap((column) => {
    const suggestion = row.suggestions[column.id];
    return suggestion ? [{ column, suggestion }] : [];
  });

export const scopeEditableFields = (tab: TabView, row: RowView): ScopeField[] =>
  tab.columns
    .filter((column) => !column.hidden && peopleEditable(column))
    .map((column) => {
      const cell: CellView = row.cells[column.id] ?? { type: 'note', note: '', origin: 'user' };
      return {
        columnId: column.id,
        column,
        key: column.key,
        label: column.label,
        cell,
        note: cellNote(cell),
      };
    });

const isFlag = (field: ScopeField): field is ScopeFlag => field.note !== null;

const newNode = (
  node: Pick<ScopeNode, 'id' | 'tabId' | 'parent' | 'depth' | 'code' | 'title'>,
): ScopeNode => ({
  ...node,
  count: 0,
  rows: 0,
  children: 0,
  packages: new Set(),
  conditions: 0,
  input: false,
  assumed: false,
  interface: false,
});

export const scopeOutline = (snapshot: SheetSnapshot): ScopeOutline => {
  const nodes: ScopeNode[] = [];
  const items: ScopeItem[] = [];
  for (const tab of scopeTabs(snapshot)) {
    const ids = new Map(tab.columns.map((column) => [column.key, column.id]));
    const cell = (row: RowView, key: string) => {
      const id = ids.get(key);
      return id === undefined ? undefined : row.cells[id];
    };
    const chapter = newNode({
      id: tab.id,
      tabId: tab.id,
      parent: null,
      depth: 0,
      code: '',
      title: tab.title,
    });
    nodes.push(chapter);
    items.push({ kind: 'heading', node: chapter, chapter, group: null });
    const stack: { node: ScopeNode; level: number; packages: string[]; disciplines: string[] }[] =
      [];
    tab.rows.forEach((row, index) => {
      while (stack.length && stack.at(-1)!.level >= row.level) stack.pop();
      const parent = stack.at(-1);
      const ownPackages = optionsOf(cell(row, 'package'));
      const ownDisciplines = optionsOf(cell(row, 'discipline'));
      const packages = ownPackages.length ? ownPackages : (parent?.packages ?? []);
      const disciplines = ownDisciplines.length ? ownDisciplines : (parent?.disciplines ?? []);
      const kind = optionsOf(cell(row, 'kind'))[0];
      const ownTitle = textOf(cell(row, 'title'));
      const description = textOf(cell(row, 'description'));
      const title = ownTitle || description;
      const next = tab.rows[index + 1];
      const hasChildren = !!next && next.level > row.level;
      const group = parent?.node ?? null;
      if (hasChildren || !kind) {
        const node = newNode({
          id: row.id,
          tabId: tab.id,
          parent: group?.id ?? chapter.id,
          depth: stack.length + 1,
          code: textOf(cell(row, 'code')),
          title,
        });
        (group ?? chapter).children++;
        nodes.push(node);
        items.push({ kind: 'heading', node, chapter, group: node });
        if (hasChildren) stack.push({ node, level: row.level, packages, disciplines });
        return;
      }
      const status = optionsOf(cell(row, 'status'));
      const titleNote = cellNote(cell(row, 'title'));
      const fields = scopeFields(tab, row);
      const note = kind === 'note';
      const excluded = status.includes('excluded');
      if (note && !excluded) (group ?? chapter).conditions++;
      const line: ScopeLine = {
        row,
        tabId: tab.id,
        code: textOf(cell(row, 'code')),
        title,
        scope: textOf(cell(row, 'scope')),
        description: ownTitle ? description : '',
        unit: textOf(cell(row, 'unit')),
        quantity: numberOf(cell(row, 'quantity')),
        quantityNote: cellNote(cell(row, 'quantity')),
        state: scopeRowState(row, ids.get('status')),
        note,
        intro: kind === 'intro',
        userAdded: note && [cell(row, 'scope'), cell(row, 'title')].some(isUserTyped),
        excluded,
        interface: packages.length > 1 || disciplines.length > 1,
        sources: referencesOf(row),
        fields,
        flags: fields.filter(isFlag),
        tags: titleNote ? [titleNote.text] : [],
      };
      items.push({ kind: 'line', line, chapter, group });
      const required =
        kind === 'post' && !status.includes('excluded') && !status.includes('by_others');
      for (const node of [chapter, ...stack.map((entry) => entry.node)]) {
        node.rows++;
        if (required) node.count++;
        if (line.state === 'input') node.input = true;
        if (line.flags.some((flag) => flag.note.kind === 'assumption')) node.assumed = true;
        if (line.interface) node.interface = true;
        for (const key of packages) node.packages.add(key);
      }
    });
  }
  return { nodes, items };
};

export const sentenceCase = (text: string, locale: string) =>
  text !== text.toLocaleUpperCase(locale) || text === text.toLocaleLowerCase(locale)
    ? text
    : text.charAt(0) + text.slice(1).toLocaleLowerCase(locale);

export const scopeLineHeadline = (line: ScopeLine, locale: string) =>
  line.scope || sentenceCase(line.title, locale);

export const scopeLineSummary = (line: ScopeLine, locale: string) => ({
  heading: line.scope ? sentenceCase(line.title, locale) : '',
  sources: line.sources.length,
  assumptions: line.flags.filter((flag) => flag.note.kind === 'assumption').length,
  inputs: line.flags.filter((flag) => flag.note.kind === 'input_needed').length,
});

type ScopeSourceGroup = { documentId: string; pages: number[]; references: Reference[] };

export const scopeSourceGroups = (sources: Reference[]): ScopeSourceGroup[] => {
  const groups = new Map<string, ScopeSourceGroup>();
  for (const reference of sources) {
    const group = groups.get(reference.documentId) ?? {
      documentId: reference.documentId,
      pages: [],
      references: [],
    };
    group.references.push(reference);
    if (reference.page !== null && !group.pages.includes(reference.page)) {
      group.pages.push(reference.page);
    }
    groups.set(reference.documentId, group);
  }
  return [...groups.values()].map((group) => ({
    ...group,
    pages: [...group.pages].sort((a, b) => a - b),
  }));
};

export const scopeSourceChips = (group: ScopeSourceGroup) => [
  ...group.pages.map((page) => {
    const reference = group.references.find((item) => item.page === page)!;
    const shapes = pageShapes(group.references, page);
    return { page, reference: shapes.length ? { ...reference, shapes } : reference };
  }),
  ...group.references
    .filter((reference) => reference.page === null)
    .map((reference) => ({ page: null, reference })),
];

export const withScopeFlagSections = (items: ScopeItem[]): ScopeItem[] => {
  const nodes = new Map<string, ScopeNode>();
  const sections = new Map<string, ScopeFlagSection>();
  for (const item of items) {
    if (item.kind !== 'heading') continue;
    nodes.set(item.node.id, item.node);
    if (item.node.depth <= 1) {
      sections.set(item.node.id, { node: item.node, assumptions: 0, inputs: 0, lines: [] });
    }
  }
  const owner = (item: ScopeItem) => {
    let node = item.group;
    while (node && node.depth > 1) node = node.parent ? (nodes.get(node.parent) ?? null) : null;
    return node ?? item.chapter;
  };
  for (const item of items) {
    if (item.kind !== 'line' || !item.line.flags.length) continue;
    const section = sections.get(owner(item).id);
    if (!section) continue;
    section.lines.push(item.line);
    for (const flag of item.line.flags) {
      if (flag.note.kind === 'input_needed') section.inputs++;
      else section.assumptions++;
    }
  }
  return items.flatMap((item) => {
    const section = item.kind === 'heading' ? sections.get(item.node.id) : undefined;
    if (!section?.lines.length) return [item];
    return [item, { kind: 'flags', section, chapter: item.chapter, group: item.group }];
  });
};

export const withScopeConditions = (items: ScopeItem[]): ScopeItem[] => {
  const conditions = new Map<string, ScopeLine[]>();
  const intros = new Map<string, ScopeLine[]>();
  for (const item of items) {
    if (item.kind !== 'line' || !(item.line.note || item.line.intro)) continue;
    const owner = (item.group ?? item.chapter).id;
    const into = item.line.intro ? intros : conditions;
    into.set(owner, [...(into.get(owner) ?? []), item.line]);
  }
  return items.flatMap((item): ScopeItem[] => {
    if (item.kind === 'line' && (item.line.note || item.line.intro)) return [];
    if (item.kind !== 'heading') return [item];
    const { chapter, group } = item;
    const intro = (intros.get(item.node.id) ?? []).map(
      (line): ScopeItem => ({ kind: 'intro', line, chapter, group }),
    );
    const lines = conditions.get(item.node.id);
    const block: ScopeItem[] = lines
      ? [{ kind: 'conditions', section: { node: item.node, lines }, chapter, group }]
      : [];
    return [item, ...intro, ...block];
  });
};

export const filterScopeOutline = (outline: ScopeOutline, query: string): ScopeOutline => {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return outline;
  const matches = (...texts: string[]) =>
    texts.some((text) => text.toLocaleLowerCase().includes(needle));
  const parents = new Map(outline.nodes.map((node) => [node.id, node.parent]));
  const matched = new Set(
    outline.nodes.filter((node) => matches(node.code, node.title)).map((node) => node.id),
  );
  const underMatch = (id: string | null) => {
    for (let node = id; node; node = parents.get(node) ?? null) if (matched.has(node)) return true;
    return false;
  };
  const kept = new Set<string>();
  const keep = (id: string | null) => {
    for (let node = id; node && !kept.has(node); node = parents.get(node) ?? null) kept.add(node);
  };
  const lines = outline.items.filter((item) => {
    if (item.kind === 'heading') {
      if (underMatch(item.node.id)) keep(item.node.id);
      return false;
    }
    if (item.kind === 'flags' || item.kind === 'conditions') return false;
    const parent = (item.group ?? item.chapter).id;
    const { line } = item;
    if (!underMatch(parent) && !scopeLineMatches(line, query)) return false;
    keep(parent);
    return true;
  });
  const shown = new Set(lines);
  return {
    nodes: outline.nodes.filter((node) => kept.has(node.id)),
    items: outline.items.filter((item) =>
      item.kind === 'heading' ? kept.has(item.node.id) : shown.has(item),
    ),
  };
};

export const visibleScopeNodes = (nodes: ScopeNode[], open: ReadonlySet<string>) => {
  const shown = new Set<string>();
  return nodes.filter((node) => {
    const visible = node.parent === null || (shown.has(node.parent) && open.has(node.parent));
    if (visible) shown.add(node.id);
    return visible;
  });
};

export const scopeNodeMarkers = (node: ScopeNode, selectedPackage: string | null) => ({
  inPackage: selectedPackage !== null && node.packages.has(selectedPackage),
  orienting: node.conditions,
  input: node.input,
  assumed: node.assumed,
  interface: node.interface,
  empty: node.rows === 0,
});

export const scopeHeadingIndex = (items: ScopeItem[], nodeId: string) =>
  items.findIndex((item) => item.kind === 'heading' && item.node.id === nodeId);

export const scopeLineIndex = (items: ScopeItem[], rowId: string) =>
  items.findIndex((item) => {
    if (item.kind === 'line' || item.kind === 'intro') return item.line.row.id === rowId;
    if (item.kind === 'conditions') return item.section.lines.some((line) => line.row.id === rowId);
    return item.kind === 'heading' && item.node.id === rowId;
  });

export const stickyScopeHeadings = (
  items: ScopeItem[],
  start: (index: number) => number,
  scrollTop: number,
  heights: { chapter: number; group: number },
) => {
  let low = 0;
  let high = items.length - 1;
  let top = -1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    if (start(middle) <= scrollTop) {
      top = middle;
      low = middle + 1;
    } else high = middle - 1;
  }
  const current = items[top];
  if (!current) return null;
  const shift = (changed: (item: ScopeItem) => boolean, height: number) => {
    for (let index = top + 1; index < items.length; index++) {
      const offset = start(index) - scrollTop - height;
      if (offset >= 0) return 0;
      if (changed(items[index]!)) return offset;
    }
    return 0;
  };
  return {
    chapter: current.chapter,
    group: current.group,
    chapterShift: shift((item) => item.chapter !== current.chapter, heights.chapter),
    groupShift: current.group
      ? shift(
          (item) => item.chapter !== current.chapter || item.group !== current.group,
          heights.chapter + heights.group,
        )
      : 0,
  };
};

export const ORIENTING_LIMIT = 6;
const ORIENTING_FIRST = 5;
export const APPLIES_FIRST = 4;

const activeConditions = (lines: ScopeLine[]) => lines.filter((line) => !line.excluded);

export const orientingVisible = (lines: ScopeLine[], showAll: boolean) =>
  lines.length > ORIENTING_LIMIT && !showAll ? lines.slice(0, ORIENTING_FIRST) : lines;

export const orientingPreview = (lines: ScopeLine[], locale: string) => {
  const titles = activeConditions(lines).map(
    (line) => sentenceCase(line.title, locale) || scopeLineHeadline(line, locale),
  );
  return { count: titles.length, titles: titles.slice(0, 3), more: Math.max(titles.length - 3, 0) };
};

export const scopeLineMatches = (line: ScopeLine, query: string) => {
  const needle = query.trim().toLocaleLowerCase();
  return (
    !!needle &&
    [line.code, line.title, line.scope, line.description].some((text) =>
      text.toLocaleLowerCase().includes(needle),
    )
  );
};

export type ScopeInherited = { node: ScopeNode; lines: ScopeLine[] };

export const inheritedConditions = (items: ScopeItem[]) => {
  const nodes = new Map<string, ScopeNode>();
  const blocks = new Map<string, ScopeLine[]>();
  for (const item of items) {
    if (item.kind === 'heading') nodes.set(item.node.id, item.node);
    if (item.kind === 'conditions') blocks.set(item.section.node.id, item.section.lines);
  }
  const inherited = new Map<string, ScopeInherited[]>();
  for (const item of items) {
    if (item.kind !== 'line' || item.line.note) continue;
    const groups: ScopeInherited[] = [];
    for (let node: ScopeNode | null = item.group ?? item.chapter; node; ) {
      const lines = activeConditions(blocks.get(node.id) ?? []);
      if (lines.length) groups.push({ node, lines });
      node = node.parent ? (nodes.get(node.parent) ?? null) : null;
    }
    if (groups.length) inherited.set(item.line.row.id, groups);
  }
  return inherited;
};

export const appliesVisible = (groups: ScopeInherited[], showAll: boolean) => {
  const total = groups.reduce((sum, group) => sum + group.lines.length, 0);
  if (showAll || total <= APPLIES_FIRST) return { groups, hidden: 0, total };
  let left = APPLIES_FIRST;
  const shown = groups.flatMap((group) => {
    if (left <= 0) return [];
    const lines = group.lines.slice(0, left);
    left -= lines.length;
    return [{ ...group, lines }];
  });
  return { groups: shown, hidden: total - APPLIES_FIRST, total };
};

export const conditionPlace = (tab: TabView, node: ScopeNode, lines: ScopeLine[]) => {
  const last = lines.at(-1);
  if (last) return { place: { after: last.row.id }, level: last.row.level };
  if (node.id === tab.id) {
    const first = tab.rows[0];
    return { place: first ? { before: first.id } : {}, level: 0 };
  }
  const row = tab.rows.find((item) => item.id === node.id);
  return { place: { after: node.id }, level: (row?.level ?? 0) + 1 };
};

export const confirmInput = (cell: CellView): CellInput | null => {
  if (cell.type === 'text' || cell.type === 'number' || cell.type === 'date')
    return { value: cell.value };
  if (cell.type === 'enum') return cell.value.length ? { value: cell.value } : null;
  return null;
};

export type ScopeConditionEdit = { rowId: string; beforeExcluded: boolean | null; text: string };

export const pendingConditionChanges = (
  edits: ScopeConditionEdit[],
  tab: TabView | undefined,
): ScopeConditionChange[] => {
  if (!tab) return [];
  const rows = new Map(tab.rows.map((row) => [row.id, row]));
  const statusId = scopeColumn(tab, 'status')?.id;
  return edits.flatMap((edit): ScopeConditionChange[] => {
    const row = rows.get(edit.rowId);
    if (!row) return [];
    const status = statusId ? row.cells[statusId] : undefined;
    const excluded = status?.type === 'enum' && status.value.includes('excluded');
    if (edit.beforeExcluded === null) return excluded ? [] : [{ kind: 'added', text: edit.text }];
    if (excluded === edit.beforeExcluded) return [];
    return [{ kind: excluded ? 'excluded' : 'restored', text: edit.text }];
  });
};

export type ScopeConditionChange = { kind: 'added' | 'excluded' | 'restored'; text: string };

export const recheckInstructions = (
  intro: string,
  changes: ScopeConditionChange[],
  words: Record<ScopeConditionChange['kind'], string>,
) => [intro, ...changes.map((change) => `- ${words[change.kind]}: "${change.text}"`)].join('\n');

export const scopePackageName = (
  pack: Pick<ScopePackage, 'displayName' | 'sourceKey' | 'origin'>,
  profile?: Pick<ScopeStandardProfile, 'chapters'>,
) => {
  const chapter =
    pack.origin === 'standard_chapter'
      ? profile?.chapters.find((entry) => entry.key === pack.sourceKey)
      : undefined;
  return chapter ? `${chapter.key} · ${pack.displayName}` : pack.displayName;
};

export const scopeRequirementOutline = (
  items: Pick<ScopeRequirement, 'id' | 'clientOrder' | 'headingPath' | 'primaryCitationId'>[],
  citations: Pick<ScopeCitation, 'id' | 'documentId'>[],
) => {
  const documents = new Map(citations.map((citation) => [citation.id, citation.documentId]));
  const groups = new Map<
    string | null,
    {
      documentId: string | null;
      itemIds: string[];
      headings: Map<string, { key: string; path: string[]; itemIds: string[] }>;
    }
  >();
  for (const item of [...items].sort((a, b) => a.clientOrder - b.clientOrder)) {
    const documentId = documents.get(item.primaryCitationId ?? '') ?? null;
    let group = groups.get(documentId);
    if (!group) {
      group = { documentId, itemIds: [], headings: new Map() };
      groups.set(documentId, group);
    }
    group.itemIds.push(item.id);
    for (let depth = item.headingPath.length ? 1 : 0; depth <= item.headingPath.length; depth++) {
      const path = item.headingPath.slice(0, depth);
      const key = JSON.stringify([documentId, ...path]);
      let heading = group.headings.get(key);
      if (!heading) {
        heading = { key, path, itemIds: [] };
        group.headings.set(key, heading);
      }
      heading.itemIds.push(item.id);
    }
  }
  return [...groups.values()].map((group) => ({
    ...group,
    headings: [...group.headings.values()],
  }));
};

export const scopePackageStep = (
  pack: import('./types').ScopePackage,
): import('./types').ScopePackageStep => {
  if (pack.status === 'offers_in' || pack.status === 'awarded' || pack.status === 'contracted')
    return 'offers';
  if (pack.status === 'sent') return 'send';
  if (!pack.requirementsCheckedAt) return 'requirements';
  if (!pack.methodKey) return 'choose-description';
  if (!pack.hasDescription) return 'work-pack';
  return 'documents';
};

export const availableScopeLevels = (citation?: ScopeCitation): ScopeLevel[] =>
  (['exact', 'paragraph', 'section', 'chapter', 'document', 'manual'] as const).filter(
    (level) =>
      level === 'exact' ||
      Boolean(
        citation &&
          (citation.expansionIds[level] ||
            (level === 'document' && citation.span.sourceFormat !== 'unpaginated')),
      ),
  );

export const changedScopeFields = (
  fields: PackageLine['fields'],
  compared?: PackageLine['fields'],
) =>
  fields.filter(
    (field) =>
      !compared?.some(
        (candidate) => candidate.key === field.key && candidate.value === field.value,
      ),
  );

export const scopeLineGroups = <T extends Pick<PackageLine, 'id' | 'chapterKey'>>(
  lines: T[],
  sections: ScopeDescription['sections'],
  grouped = true,
) => {
  if (!lines.length) return [];
  if (!grouped) return [{ key: null, section: undefined, lines }];
  const groups = new Map<string | null, T[]>();
  for (const line of lines) {
    const key = line.chapterKey || null;
    const group = groups.get(key) ?? [];
    group.push(line);
    groups.set(key, group);
  }
  const keys = new Set([...sections.map((section) => section.key), ...groups.keys()]);
  return [...keys].flatMap((key) => {
    const group = groups.get(key);
    return group
      ? [{ key, section: sections.find((section) => section.key === key), lines: group }]
      : [];
  });
};

export const scopeMarketTerm = (
  profile: import('./types').ScopeStandardProfile | undefined,
  plural: boolean,
  fallback: string,
  locale: string,
  title = false,
) => {
  const term = profile?.terms.find((entry) => entry.key === 'priced_line');
  const value = (plural ? term?.plural : term?.singular) || fallback;
  if (title) return value.charAt(0).toLocaleUpperCase(locale) + value.slice(1);
  if (/[A-Z].*[A-Z]/.test(value.split(' ')[0] ?? '')) return value;
  return value.charAt(0).toLocaleLowerCase(locale) + value.slice(1);
};

export const scopeEditorFields = (fields: PackageLine['fields']) =>
  fields.filter(
    (field) =>
      ![
        'code',
        'title',
        'description',
        'quantity',
        'unit',
        'rate',
        'amount',
        'summary',
        'location',
      ].includes(field.key),
  );

export const scopeLineAmount = (line: Pick<PackageLine, 'quantity' | 'unitPrice'>) =>
  line.quantity === null || line.unitPrice == null ? null : line.quantity * line.unitPrice;

export const scopeCheckpointTarget = (location: string, lines: PackageLine[]) => {
  const index = lines.findIndex((line) => location.includes(line.id));
  const codeIndex = lines.findIndex((line) => line.code && location.includes(line.code));
  const number = /(?:^|\s)(\d+)\s*·/.exec(location)?.[1];
  const pathIndex = /(?:lines|posts)\[(\d+)\]/.exec(location)?.[1];
  const lineIndex =
    index >= 0
      ? index
      : pathIndex !== undefined
        ? Number(pathIndex)
        : number
          ? Number(number) - 1
          : codeIndex >= 0
            ? codeIndex
            : null;
  const key = location.split(/[.·]/).at(-1)?.trim() ?? '';
  const internal = /[0-9a-f]{8}-[0-9a-f-]{27,}|(?:lines|posts)\[|_[a-z]|\.[a-z]/i.test(location);
  return { lineIndex, key, label: internal ? null : location };
};

export const scopeContentPackageIds = (
  items: Pick<ScopeRequirement, 'workPackageId' | 'status'>[],
  lines: Pick<PackageLine, 'workPackageId' | 'removed'>[],
): Set<string> =>
  new Set([
    ...items.filter((item) => item.status !== 'excluded').map((item) => item.workPackageId),
    ...lines.filter((line) => !line.removed).map((line) => line.workPackageId),
  ]);

export const scopeSourceMatchesCitation = (
  source: ScopeSource,
  citation: ScopeCitation,
  linked = false,
) =>
  ((Boolean(citation.recordKey) && source.reference === citation.recordKey) || linked) &&
  (source.documentId === null || source.documentId === citation.documentId) &&
  (source.page === null ||
    (citation.span.pageFrom <= source.page && citation.span.pageTo >= source.page));

export const scopeDocumentSources = (sources: ScopeSource[], citations: ScopeCitation[] = []) => {
  const groups = new Map<string, { documentId: string | null; pages: number[] }>();
  for (const source of sources) {
    const recordDocuments = new Set(
      citations
        .filter((citation) => citation.recordKey === source.reference)
        .map((citation) => citation.documentId),
    );
    const documentId =
      source.documentId ?? (recordDocuments.size === 1 ? [...recordDocuments][0]! : null);
    const key = documentId ?? source.reference;
    const group = groups.get(key) ?? { documentId, pages: [] };
    if (source.page !== null && !group.pages.includes(source.page)) group.pages.push(source.page);
    groups.set(key, group);
  }
  return [...groups.values()].map((group) => ({
    ...group,
    pages: group.pages.sort((a, b) => a - b),
  }));
};

export const scopeSourceCitations = (
  sources: ScopeSource[],
  citations: ScopeCitation[],
  contextCitationIds: (string | null)[],
): ScopeCitation[] => {
  const context = new Set(contextCitationIds);
  return citations.filter(
    (citation) =>
      context.has(citation.id) ||
      sources.some(
        (source) =>
          citation.recordKey != null &&
          source.reference === citation.recordKey &&
          (source.documentId === null || source.documentId === citation.documentId),
      ),
  );
};

export const scopeChoiceCode = (
  line: Pick<PackageLine, 'fields' | 'codeVariants'>,
): string | undefined =>
  line.codeVariants?.find(
    (variant) =>
      variant.choices.length > 0 &&
      variant.choices.every((choice) =>
        line.fields.some((field) => field.key === choice.fieldKey && field.value === choice.value),
      ),
  )?.code;

export const scopeChapterNumber = (chapter: { key: string; documentId: string }): string => {
  const prefix = `${chapter.documentId}:`;
  return chapter.key.startsWith(prefix) ? chapter.key.slice(prefix.length) : '';
};

export const scopeFieldLabel = (
  field: PackageLine['fields'][number],
  layout: { key: string; label: string }[],
): string | undefined => {
  const label = field.label || layout.find((entry) => entry.key === field.key)?.label;
  return label && label !== field.key ? label : undefined;
};

export type ScopeFlowMode = 'performance' | 'itemised' | 'execution';

export const SCOPE_FLOW_STEPS = {
  performance: [
    'overview',
    'requirements',
    'choose-description',
    'work-pack',
    'documents',
    'generate',
    'proofread',
    'send',
    'offers',
  ],
  itemised: [
    'overview',
    'requirements',
    'choose-description',
    'work-pack',
    'add-information',
    'documents',
    'generate',
    'proofread',
    'send',
    'offers',
  ],
  execution: ['overview', 'client-posts', 'add-information', 'documents', 'generate', 'send'],
} as const satisfies Record<ScopeFlowMode, readonly ('overview' | ScopePackageStep)[]>;

export const scopeFlowMode = (
  data: import('./types').ScopeBreakdown,
  profile: import('./types').ScopeStandardProfile | undefined,
  pack: import('./types').ScopePackage | undefined,
): ScopeFlowMode => {
  if (data.breakdown.startMode === 'client_posts') return 'execution';
  const method = profile?.descriptionMethods.find((entry) => entry.key === pack?.methodKey);
  return method?.family === 'itemised' ? 'itemised' : 'performance';
};

export const scopeNextStep = (
  mode: ScopeFlowMode,
  current: 'overview' | ScopePackageStep,
): 'overview' | ScopePackageStep | null => {
  if (current === 'proofread') return 'send';
  if (current === 'send') return 'offers';
  if (current === 'changes') return 'add-information';
  if (mode === 'execution' && current === 'generate') return null;
  if (current === 'choose-description') return null;
  const steps: readonly ('overview' | ScopePackageStep)[] = SCOPE_FLOW_STEPS[mode];
  const index = steps.indexOf(current);
  return index < 0 ? null : (steps[index + 1] ?? null);
};

export const scopeWorkflow = (
  mode: ScopeFlowMode,
  pack: Pick<
    ScopePackage,
    | 'status'
    | 'version'
    | 'hasDescription'
    | 'methodKey'
    | 'requirementsCheckedAt'
    | 'descriptionState'
  >,
  generation: import('./types').ScopePackageGeneration | null | undefined,
  documents: Pick<import('./types').ScopePackageDocument, 'state'>[],
  clientPostCount: number,
) => {
  const sent = ['sent', 'offers_in', 'awarded', 'contracted'].includes(pack.status);
  const exported = Boolean(
    generation?.state === 'succeeded' &&
      generation.snapshot?.version === pack.version &&
      generation.snapshot.inputHash === generation.inputHash &&
      generation.snapshot.files.some((file) => file.kind === 'merged'),
  );
  const described = mode === 'execution' ? clientPostCount > 0 : pack.hasDescription;
  const describing = mode !== 'execution' && scopeDescribing(pack.descriptionState);
  const advanced = described || exported || sent;
  const done: Partial<Record<'overview' | ScopePackageStep, boolean>> = {
    requirements: Boolean(pack.requirementsCheckedAt),
    'choose-description': Boolean(pack.methodKey),
    'work-pack': described,
    'client-posts': described,
    documents:
      exported || (documents.length > 0 && documents.every((doc) => doc.state !== 'empty')),
    generate: exported,
    proofread: pack.status !== 'draft',
    send: sent,
    offers: ['offers_in', 'awarded', 'contracted'].includes(pack.status),
  };
  const required: Partial<Record<'overview' | ScopePackageStep, ScopePackageStep>> = {};
  if (mode !== 'execution' && !pack.requirementsCheckedAt && !advanced)
    required['choose-description'] = 'requirements';
  const failed = mode !== 'execution' && pack.descriptionState === 'failed';
  if (
    mode !== 'execution' &&
    (!pack.methodKey || (!described && !describing && !failed)) &&
    !exported &&
    !sent
  )
    for (const step of ['work-pack', 'add-information', 'documents', 'generate'] as const)
      required[step] = 'choose-description';
  if ((describing || (failed && !described)) && !exported && !sent)
    for (const step of ['add-information', 'documents', 'generate'] as const)
      required[step] = 'work-pack';
  if (mode === 'execution' && !described)
    for (const step of ['add-information', 'documents', 'generate'] as const)
      required[step] = 'client-posts';
  if (!exported && !sent) {
    required.proofread = 'generate';
    required.send = 'generate';
  }
  if (!sent) required.offers = 'send';
  if (scopeBusyBuild(generation?.state)) delete required.generate;
  return { done, required, describing };
};

export const scopeDefaultFormat = (
  document: { sectionKey: string; fileIds: string[] },
  saved: import('./types').ScopeGeneratePackageRequest | null | undefined,
  formats: { key: string }[] = [],
) => {
  const file = saved?.files?.find((entry) => entry.sectionKey === document.sectionKey);
  if (file) return file.formatKey;
  return document.fileIds.length ? null : (formats[0]?.key ?? null);
};

export const scopeDescribing = (state: string | null | undefined) =>
  state === 'queued' || state === 'generating';

export const scopeBusyBuild = (state: string | undefined) =>
  ['dispatching', 'running', 'retrying', 'unknown'].includes(state ?? '');

export const scopeApiError = (error: unknown) => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return detail && typeof detail === 'object' && 'code' in detail
    ? (detail as ScopeApiError)
    : null;
};

export const scopeErrorCode = (error: unknown) => scopeApiError(error)?.code ?? null;

export const scopeLevelLabel = (level: ScopeLevel) =>
  t(
    {
      exact: { en: 'Exact', nb: 'Eksakt' },
      paragraph: { en: 'Paragraph', nb: 'Avsnitt' },
      section: { en: 'Section', nb: 'Seksjon' },
      chapter: { en: 'Chapter', nb: 'Kapittel' },
      document: { en: 'Document', nb: 'Dokument' },
      manual: { en: 'Manual', nb: 'Manuelt' },
    }[level],
  );

export const scopeStepLabel = (step: Exclude<ScopePackageStep, 'client-posts'>) =>
  t(
    {
      requirements: { en: 'Requirements', nb: 'Krav' },
      'choose-description': { en: 'Choose description', nb: 'Velg beskrivelse' },
      'work-pack': { en: 'Work pack', nb: 'Arbeidspakke' },
      'add-information': { en: 'Add information', nb: 'Legg til informasjon' },
      documents: { en: 'Work pack documents', nb: 'Dokumenter i arbeidspakken' },
      generate: { en: 'Generate work pack', nb: 'Generer arbeidspakke' },
      proofread: { en: 'Proofread and edit', nb: 'Gjennomlesning og tilpasning' },
      send: { en: 'Send', nb: 'Send' },
      offers: { en: 'Offers', nb: 'Tilbud' },
      changes: { en: 'Changes against the client', nb: 'Endringer mot byggherren' },
    }[step],
  );

export const scopePackageStatusLabel = (status: ScopePackage['status']) =>
  t(
    {
      draft: { en: 'Draft', nb: 'Utkast' },
      ready: { en: 'Ready', nb: 'Klar' },
      sent: { en: 'Sent', nb: 'Sendt' },
      offers_in: { en: 'Offers received', nb: 'Tilbud mottatt' },
      awarded: { en: 'Awarded', nb: 'Tildelt' },
      contracted: { en: 'Contracted', nb: 'Kontrakt inngått' },
    }[status],
  );

export const scopeRowStateLabel = (state: ScopeRowState) =>
  t(
    {
      stated: { en: 'Stated in the documents', nb: 'Oppgitt i dokumentene' },
      assumed: { en: 'Assumed value', nb: 'Antatt verdi' },
      input: { en: 'Needs input', nb: 'Trenger avklaring' },
      unsourced: { en: 'No source', nb: 'Ingen kilde' },
    }[state],
  );

export const scopeStructureTitle = (kind: ScopeStructureKind) =>
  t(
    {
      library: { en: 'A standard', nb: 'En standard' },
      volve: { en: 'Volve’s template', nb: 'Volves mal' },
      template: { en: 'Your template', nb: 'Egen mal' },
      tender_bill: { en: 'Tender’s own quantity list', nb: 'Anbudets egen prisliste' },
    }[kind],
  );

export const scopeErrorText = (error: ScopeApiError | null | undefined) => {
  const source = error?.documentName ?? '';
  const failed = {
    en: 'Something went wrong at Volve. Try again.',
    nb: 'Noe gikk galt hos Volve. Prøv igjen.',
  };
  const known: Record<string, { en: string; nb: string }> = {
    source_not_ready: {
      en: `A source (${source}) has not been read yet. Try again shortly, or upload it yourself.`,
      nb: `En kilde (${source}) er ikke lest inn ennå. Prøv igjen om litt, eller last opp selv.`,
    },
    source_missing: {
      en: `A source (${source}) is no longer in the workspace.`,
      nb: `En kilde (${source}) finnes ikke lenger i arbeidsområdet.`,
    },
    level_not_read: {
      en: 'A requirement points to a source level Volve has not read. Pick another level on the requirement.',
      nb: 'Et krav peker på et kildenivå Volve ikke har lest. Velg et annet nivå på kravet.',
    },
    execution_missing: {
      en: 'Choose self-perform or subcontract first.',
      nb: 'Velg egen utførelse eller underentreprise først.',
    },
    description_busy: {
      en: 'The description is being made. Try again when it is done.',
      nb: 'Beskrivelsen lages nå. Prøv igjen når den er ferdig.',
    },
    build_busy: {
      en: 'Something else in the package is being made. Try again when it is done.',
      nb: 'Noe annet i pakken lages nå. Prøv igjen når det er ferdig.',
    },
    worker_failed: failed,
  };
  const text = known[error?.code ?? ''];
  if (text) return t(text);
  return error?.message || t(failed);
};

export const scopeFieldState = ({ status = 'known', value }: PackageLine['fields'][number]) =>
  status === 'known' && !value ? 'empty' : status;

export const scopeLineQuestions = (line: Pick<PackageLine, 'notes'>) =>
  line.notes.filter((note) => note.kind === 'user_must_select' && note.options.length > 0);

export const scopeAnswerQuestion = <
  T extends Pick<PackageLine, 'notes' | 'fields' | 'code' | 'codeVariants'>,
>(
  line: T,
  fieldKey: string,
  value: string,
): T => {
  const answered = {
    ...line,
    fields: line.fields.some((field) => field.key === fieldKey)
      ? line.fields.map((field) =>
          field.key === fieldKey ? { ...field, value, status: 'known' as const } : field,
        )
      : [...line.fields, { key: fieldKey, value, status: 'known' as const }],
    notes: line.notes.filter((note) => note.fieldKey !== fieldKey),
  };
  return { ...answered, code: scopeChoiceCode(answered) ?? line.code };
};

const GLYPHS = '\uf0b7\uf0a7\uf076\uf0d8\uf0a8\uf0fc\uf0e0\u25aa\u25cf';
const SCOPE_BULLETS = new RegExp(`\\s*[${GLYPHS}]\\s*`, 'g');
const GLYPH = new RegExp(`[${GLYPHS}]`);
const HEADING_NEXT = new RegExp(`^(\\p{Lu}|[•${GLYPHS}])`, 'u');

type ScopeTextBlock = { kind: 'text'; text: string } | { kind: 'list'; items: string[] };

export const scopeTextBlocks = (text: string): ScopeTextBlock[] => {
  const blocks: ScopeTextBlock[] = [];
  for (const raw of text.replace(SCOPE_BULLETS, '\n• ').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const last = blocks.at(-1);
    if (line.startsWith('• ')) {
      const item = line.slice(2).trim();
      if (!item) continue;
      if (last?.kind === 'list') last.items.push(item);
      else blocks.push({ kind: 'list', items: [item] });
    } else if (last?.kind === 'list' && /^[a-zæøå0-9(]/.test(line))
      last.items[last.items.length - 1] += ` ${line}`;
    else if (last?.kind === 'text') last.text += ` ${line}`;
    else blocks.push({ kind: 'text', text: line });
  }
  return blocks;
};

export const scopeTextHeading = (paragraph: string) => {
  const [first = '', next = ''] = paragraph.trim().split('\n');
  const heading = first.trim();
  return heading.length > 0 &&
    heading.length < 80 &&
    !/[.:;,]$/.test(heading) &&
    !heading.startsWith('•') &&
    !GLYPH.test(heading) &&
    HEADING_NEXT.test(next.trim())
    ? heading
    : null;
};

export const scopeItemText = (item: Pick<ScopeRequirement, 'text' | 'displayText' | 'userText'>) =>
  item.displayText ?? item.userText ?? item.text;

export const scopeLinkedRequirements = (
  line: Pick<PackageLine, 'childItemIds'>,
  items: ScopeRequirement[],
  startMode: import('./types').ScopeStartMode,
) => {
  const groups: { item: ScopeRequirement; ids: string[] }[] = [];
  for (const item of items) {
    if (!line.childItemIds.includes(item.id)) continue;
    if (startMode === 'client_posts' && item.origin === 'bill') continue;
    const text = item.userText ?? item.text;
    const same = groups.find((group) => (group.item.userText ?? group.item.text) === text);
    if (same) same.ids.push(item.id);
    else groups.push({ item, ids: [item.id] });
  }
  return groups;
};
